import { useFormSubmit } from "@/hooks/useFormSubmit";

interface InquiryFormProps {
  title?: string;
  description?: string;
  submitLabel?: string;
}

const serviceOptions = [
  "Attend",
  "Cook",
  "Host",
  "Speak",
  "Demonstrate",
  "Collaborate",
  "Other",
];

const eventTypes = [
  "Festival",
  "Culinary Event",
  "Restaurant Opening",
  "Corporate Event",
  "Food Experience",
  "Community Event",
  "Private Event",
  "Other",
];

export default function InquiryForm({
  title = "Send an inquiry",
  description = "Tell us where, when and what you have in mind. Eddie's team reads every message.",
  submitLabel = "Send Inquiry",
}: InquiryFormProps) {
  const endpoint = "https://readdy.ai/api/form/db23tapggrtvtk1bh0i0";
  const { status, message, handleSubmit } = useFormSubmit(endpoint);

  return (
    <div className="w-full">
      <h2 className="font-heading text-3xl leading-tight text-foreground-950 md:text-4xl">
        {title}
      </h2>
      <p className="mt-4 max-w-xl font-body text-base leading-relaxed text-foreground-700">
        {description}
      </p>

      <form
        id="chef-eddie-inquiry"
        data-readdy-form
        onSubmit={handleSubmit}
        noValidate
        className="mt-10"
      >
        <div className="grid grid-cols-1 gap-x-8 gap-y-7 md:grid-cols-2">
          <div>
            <label htmlFor="inq-name" className="field-label">
              Name
            </label>
            <input
              id="inq-name"
              name="name"
              type="text"
              required
              autoComplete="name"
              placeholder="Your full name"
              className="field-input"
            />
          </div>

          <div>
            <label htmlFor="inq-email" className="field-label">
              Email
            </label>
            <input
              id="inq-email"
              name="email"
              type="email"
              required
              autoComplete="email"
              placeholder="you@example.com"
              className="field-input"
            />
          </div>

          <div>
            <label htmlFor="inq-phone" className="field-label">
              Phone
            </label>
            <input
              id="inq-phone"
              name="phone"
              type="tel"
              autoComplete="tel"
              placeholder="Optional"
              className="field-input"
            />
          </div>

          <div>
            <label htmlFor="inq-org" className="field-label">
              Organization
            </label>
            <input
              id="inq-org"
              name="organization"
              type="text"
              autoComplete="organization"
              placeholder="Company, venue or group"
              className="field-input"
            />
          </div>

          <div>
            <label htmlFor="inq-event-name" className="field-label">
              Event / Project Name
            </label>
            <input
              id="inq-event-name"
              name="eventName"
              type="text"
              placeholder="What's it called?"
              className="field-input"
            />
          </div>

          <div>
            <label htmlFor="inq-location" className="field-label">
              Location
            </label>
            <input
              id="inq-location"
              name="location"
              type="text"
              placeholder="City, country"
              className="field-input"
            />
          </div>

          <div>
            <label htmlFor="inq-date" className="field-label">
              Date
            </label>
            <input
              id="inq-date"
              name="eventDate"
              type="date"
              className="field-input"
            />
          </div>

          <div>
            <label htmlFor="inq-type" className="field-label">
              Type of Event
            </label>
            <select id="inq-type" name="eventType" defaultValue="" className="field-select">
              <option value="" disabled>
                Select a type
              </option>
              {eventTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="inq-guests" className="field-label">
              Number of Guests
            </label>
            <input
              id="inq-guests"
              name="guests"
              type="text"
              inputMode="numeric"
              placeholder="Approximate"
              className="field-input"
            />
          </div>
        </div>

        <fieldset className="mt-9">
          <legend className="field-label">
            What would you like Eddie to do?
          </legend>
          <div className="mt-1 flex flex-wrap gap-2">
            {serviceOptions.map((o) => (
              <label key={o} className="cursor-pointer">
                <input
                  type="checkbox"
                  name="services"
                  value={o}
                  className="peer sr-only"
                />
                <span className="inline-block rounded-full border border-foreground-950/20 px-4 py-2 font-label text-[10px] uppercase tracking-label text-foreground-600 transition-all duration-300 hover:border-foreground-950/40 peer-checked:border-primary-500 peer-checked:bg-primary-500 peer-checked:text-background-50">
                  {o}
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="mt-9">
          <label htmlFor="inq-details" className="field-label">
            Additional Information
          </label>
          <textarea
            id="inq-details"
            name="details"
            rows={5}
            maxLength={500}
            placeholder="Tell us the story behind the event, and what would make it special (500 characters max)."
            className="field-textarea"
          />
        </div>

        <input
          type="text"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          className="form-aux"
        />

        <div className="mt-10 flex flex-col gap-5 sm:flex-row sm:items-center">
          <button
            type="submit"
            disabled={status === "submitting"}
            className="btn-primary disabled:cursor-not-allowed disabled:opacity-60"
          >
            {status === "submitting" ? "Sending..." : submitLabel}
            {status !== "submitting" && (
              <i className="ri-send-plane-line text-base" />
            )}
          </button>

          {status === "success" && (
            <p className="flex items-center gap-2 font-body text-sm text-primary-700">
              <i className="ri-check-line text-base" />
              {message}
            </p>
          )}
          {status === "error" && (
            <p className="flex items-center gap-2 font-body text-sm text-red-700">
              <i className="ri-error-warning-line text-base" />
              {message}
            </p>
          )}
        </div>
      </form>
    </div>
  );
}